import {
    ChangeDetectorRef,
    Component,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { FNStatus } from 'app/modules/corrective-maintenance/model';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { handleFormatDate } from 'app/core/shared/helper/date.helper';
import { FailureNotificationService } from 'app/modules/corrective-maintenance/services/failure-notification.service';
import { ActivatedRoute } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { EMPTY, map, merge, Subject, switchMap, take, takeUntil } from 'rxjs';
import { FnModalComponent } from 'app/modules/corrective-maintenance/component/fn-modal/fn-modal.component';
import module from "app/modules/corrective-maintenance/corrective-maintenance.module";
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { FuseConfirmationService } from '@fuse/services/confirmation';
import { DEFAULT_COLUMN_LABELS } from 'app/core/shared/constant/column-label';
import { ApplySortPayload } from 'app/core/shared/component/sort-menu/sort-menu.component';

@Component({
    selector: 'app-cm-failure-notification',
    templateUrl: './failure-notification.page.html',
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: module,
    styleUrls: ['./failure-notification.page.scss'],
})
export class FailureNotificationPage {
    @ViewChild(MatPaginator) private _paginator: MatPaginator;
    @ViewChild(MatSort) private _sort: MatSort;
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    searchForm: UntypedFormGroup;
    dt: any
    startingIndex: number = 0
    isLoading: boolean = false
    status = FNStatus;

    sortInfo: string;
    private columnLabels: { [key: string]: string } = {
        ...DEFAULT_COLUMN_LABELS,
        failureDate: 'Failure Date',
        failEquipment: 'Failed Equipment',
        problemReported: 'Reported Problem',
        riskRanking: 'Risk Ranking',
        status: 'Status',
    };

    columnSort = Object.entries(this.columnLabels).map(([key, name]) => ({
        key,
        name,
        isChecked: false, // default unchecked
      }));

    constructor(
        private service: FailureNotificationService,
        private formBuilder: UntypedFormBuilder,
        private dialog: MatDialog,
        private activeRoute: ActivatedRoute,
        private _changeDetectorRef: ChangeDetectorRef,
        private _fuseConfirmationService: FuseConfirmationService,
    ) { }

    ngOnInit() {
        this.setSearch();
        this.setDataTable();
        this.setPage({ offset: 0 });

        this.activeRoute.queryParamMap.pipe(take(1)).subscribe((params) => {
            const action = params.get('action');
            const id = params.get('id');
            this.autoloadDialog({ id, action });
        });
    }

    async autoloadDialog({ id, action }) {
        let payload = null;
        if (action == 'create') {
            payload = {};
        }

        if (action == 'edit' && id) {
            const getFN = () => {
                return new Promise((resolve, reject) => {
                    this.service.getFailureNotificationById(id).subscribe((response) => {
                        resolve(response.data);
                    })
                });
            }
            payload = await getFN();
        }
        if (payload) this.openDialog({ payload, action });
    }

    ngAfterViewInit() {
        if (this._sort && this._paginator) {
            // Set the initial sort
            this._sort.sort({
                id: 'field',
                start: 'asc',
                disableClear: true,
            });

            // Mark for check
            this._changeDetectorRef.markForCheck();

            // If the user changes the sort order...
            this._sort.sortChange
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe(() => {
                    // Reset back to the first page
                    this._paginator.pageIndex = 0;

                    this.onSort(this._sort)
                });

            // Get platform types if sort or page changes
            merge(this._sort.sortChange, this._paginator.page).pipe(
                switchMap(() => {
                    // this.isLoading = true;

                    let pageInfo = {
                        'count': this._paginator.length,
                        'pageSize': this._paginator.pageSize,
                        'limit': this._paginator.pageSize,
                        'offset': this._paginator.pageIndex
                    }
                    this.setPage(pageInfo); // Emit page number

                    return EMPTY
                }),
                map(() => {
                    this.isLoading = false;
                }),
            ).subscribe();
        }
    }

    onSort(sort: any) {
        if (!sort) return;
        this.searchForm.patchValue({
            OrderBy1: sort.active,
            OrderDir: sort.direction,
        });

        this.sortInfo = this.columnLabels[sort.active] || sort.active;
        this.setPage({ offset: 0 });
    }

    handleSearch(event$) {
        const field = event$?.Field;
        const platform = event$?.Platform;
        const well = event$?.Well;
        const string = event$?.String;
    
        if ([field, platform, well, string].some(v => v != null && v !== '')) {
          this.setPage({ offset: 0 });
        }
      }

    setSearch() {
        this.sortInfo = 'Updated Date'
        this.searchForm = this.formBuilder.group({
            Page: [0],
            PageSize: [20],
            OrderBy1: [null],
            OrderBy2: [null],
            OrderBy3: [null],
            OrderDir: 'asc',
            WellName: [''],
            PlatformName: [''],
            StringName: [''],
            FieldName: [''],
        });

        this.searchForm.get('PageSize').valueChanges.subscribe(value => this.setSize(value));
    }

    setSize(size: number) {
        this.dt.page.size = size;
        this.dt.page.offset = 0
        this.setPage({ offset: this.dt.page.offset });
    }

    downloadFnExcel() {
        this.service.downloadExcel()
    }

    setPage(pageInfo) {
        this.dt.page.offset = pageInfo.offset;
        this.startingIndex = this.dt.page.offset * this.dt.page.size

        this.searchForm.patchValue({
            Page: this.dt.page.offset + 1,
        });

        const params = this.searchForm.value;

        const search: any = {
            wellLongName: params.Well?.wellLongName,
            stringShortName: params.String?.stringShortName,
            platformLongName: params.Platform?.platformLongName,
            fieldLongName: params.Field?.fieldLongName,
        };

        this.service
            .getFailureNotification({ ...params, search })
            .subscribe((pagedData) => {
                this.dt.page.totalPages = pagedData.data.totalPages;
                this.dt.page.totalRecords = pagedData.data.totalRecords;
                this.dt.rows = pagedData.data.data;
            });
    }

    setDataTable() {
        this.dt = {
            page: {
                size: this.searchForm.get('PageSize')?.value,
                totalRecords: 0,
                totalPages: 0,
                offset: 0,
            },
            rows: [],
        };
    }

    openDialog({ payload, action }) {
        const dialogRef = this.dialog.open(FnModalComponent, {
            data: { payload, action },
            width: '70%',
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (result.isUpdate) {
                this.setPage({ offset: this.dt.page.offset })
            }
        });
    }

    reset() {
        this.setSearch();
        this.setPage({ offset: 0 });
    }

    handleFormatDate(dateString: any, withTime: boolean) {
        return handleFormatDate(dateString, withTime)
    }

    deleteFailureNotification(id) {
        // Open the confirmation dialog
        const confirmation = this._fuseConfirmationService.open({
            title: 'Delete Failure Notification Record',
            message: 'Are you sure you want to delete this Failure Notification?',
            actions: {
                confirm: {
                    label: 'Delete',
                },
            },
        });

        confirmation.afterClosed().subscribe((result) => {
            // If the confirm button pressed...
            if (result === 'confirmed') {
                this.service.deleteFailureNotification(id).subscribe((response) => {
                    this.setPage({ offset: this.dt.page.offset })
                });
            }
        })
    }

    applySort(payload: ApplySortPayload) {
        // Patch form (fill missing with null)
        const [OrderBy1, OrderBy2, OrderBy3] = [
          payload.columns[0]?.key ?? null,
          payload.columns[1]?.key ?? null,
          payload.columns[2]?.key ?? null,
        ];
    
        this.searchForm.patchValue({
          OrderBy1,
          OrderBy2,
          OrderBy3,
          OrderDir: payload.orderDir ?? 'asc',
        });
        
        const labels = payload.columns.map(k => k.name);
        this.sortInfo = labels.length ? labels.join(' → ') : 'No sorting';
        this.setPage({ offset: 0 });
      }
}
